import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import moment from "moment";
import Links from "../../Data/Links";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import "./MetaMtdReport.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const COLUMNS = [
  { key: "name", label: "Dealership" },
  { key: "budget", label: "Budget" },
  { key: "spent", label: "Spent" },
  { key: "budgetPace", label: "Budget Pace" },
  { key: "leadGoal", label: "Lead Goal" },
  { key: "leads", label: "Leads" },
  { key: "leadsPercent", label: "Leads %" },
  { key: "cplGoal", label: "CPL Goal" },
  { key: "cpl", label: "CPL" }
];

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const toTargetNumber = (value) => {
  if (value === null || value === undefined || value === "") return undefined;
  const num = Number(value);
  return Number.isFinite(num) ? num : undefined;
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const formatNumberOne = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatPercent = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return `${num.toFixed(1)}%`;
};

const normalizeStoreKey = (value) => String(value || "").trim().toLowerCase();

const getStoreName = (row) => {
  return (
    row?.store_name ||
    row?.STORE_NAME ||
    row?.store ||
    row?.dealership ||
    row?.name ||
    row?.storeName ||
    "-"
  );
};

const getDivisionLabel = (row) => {
  return (
    row?.division ||
    row?.store_division ||
    row?.storeDivision ||
    row?.Division ||
    ""
  ).trim();
};

const usedDivisions = ["DriveNation", "FFUN Cars", "Used", "Carooga", "FFUN Group", "Group"];
const usedStoreNames = ["Carooga", "FFUN Group"];

const normalizeDivision = (divisionValue) => {
  const division = (divisionValue || "").trim();
  if (!division) return "";
  if (usedDivisions.includes(division)) return "Used";
  return "OEM";
};

export default function MetaMtdReport() {
  const [rows, setRows] = useState([]);
  const [targetsByStore, setTargetsByStore] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedStores, setSelectedStores] = useState(new Set());
  const [visibleColumns, setVisibleColumns] = useState(new Set(COLUMNS.map((col) => col.key)));
  const [storeMenuOpen, setStoreMenuOpen] = useState(false);
  const [columnMenuOpen, setColumnMenuOpen] = useState(false);
  const storeMenuRef = useRef(null);
  const columnMenuRef = useRef(null);
  const hasInitializedStores = useRef(false);

  const startDate = useMemo(() => moment().startOf("month"), []);
  const endDate = useMemo(() => {
    const yesterday = moment().subtract(1, "day");
    return yesterday.isBefore(startDate) ? startDate.clone() : yesterday;
  }, [startDate]);

  const totalDays = endDate.daysInMonth();
  const elapsedDays = endDate.date();
  const paceFactor = totalDays ? elapsedDays / totalDays : 0;

  const dateLabel = `${startDate.format("MMM D")} - ${endDate.format("D, YYYY")}`;
  const targetMonth = startDate.format("YYYY-MM");

  const fetchRows = () => {
    setLoading(true);
    setError("");

    const payload = {
      start_date: startDate.format("YYYY-MM-DD"),
      end_date: endDate.format("YYYY-MM-DD")
    };

    axios
      .post(Links.metaAdsDivisionsDaily, payload)
      .then((res) => {
        setRows(res.data?.data || res.data || []);
      })
      .catch((err) => {
        console.log("Meta MTD fetch error:", err);
        setRows([]);
        setError("Failed to load Meta MTD data.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchRows();
  }, []);

  useEffect(() => {
    if (rows.length === 0) {
      setTargetsByStore(new Map());
      return;
    }

    const storeDivisionPairs = new Map();
    rows.forEach((row) => {
      const storeName = getStoreName(row);
      if (!storeName || storeName === "-") return;
      const storeKey = normalizeStoreKey(storeName);
      if (!storeDivisionPairs.has(storeKey)) {
        storeDivisionPairs.set(storeKey, {
          name: storeName,
          division: normalizeDivision(getDivisionLabel(row)) || "OEM"
        });
      }
    });

    const entries = Array.from(storeDivisionPairs.entries());
    if (entries.length === 0) {
      setTargetsByStore(new Map());
      return;
    }

    Promise.allSettled(
      entries.map(([, meta]) =>
        axios.post(Links.marketingTargets, {
          division: meta.division,
          dealer: meta.name,
          month: targetMonth
        })
      )
    )
      .then((results) => {
        const next = new Map();
        results.forEach((result, index) => {
          if (result.status !== "fulfilled") return;
          const storeKey = entries[index][0];
          const payload = result.value?.data?.data ?? result.value?.data ?? {};
          const targetRow = Array.isArray(payload) ? payload[0] : payload;
          if (!targetRow) return;

          const budget = toTargetNumber(
            targetRow?.meta_ads_budget || targetRow?.google_ads_budget || targetRow?.budget
          );
          const leadGoal = toTargetNumber(
            targetRow?.meta_ads_leads_goal || targetRow?.google_ads_leads_goal || targetRow?.leads_goal
          );

          if (budget !== undefined || leadGoal !== undefined) {
            next.set(storeKey, { budget, leadGoal });
          }
        });
        setTargetsByStore(next);
      })
      .catch((err) => {
        console.log("Meta MTD targets error:", err);
        setTargetsByStore(new Map());
      });
  }, [rows, targetMonth]);

  const getDivisionGroup = (row) => {
    const divisionLabel = getDivisionLabel(row);
    const normalized = normalizeDivision(divisionLabel);
    if (normalized) return normalized;

    const storeName = getStoreName(row);
    if (storeName) {
      const normalizedStore = storeName.trim().toLowerCase();
      const isUsedStore = usedStoreNames.some(
        (name) => name.toLowerCase() === normalizedStore || normalizedStore.includes(name.toLowerCase())
      );
      if (isUsedStore) return "Used";
    }

    return "OEM";
  };

  const buildDerivedMetrics = (entry) => {
    const leadGoal = entry.leadGoal || 0;
    const cplGoal = leadGoal ? entry.budget / leadGoal : 0;
    const cpl = entry.leads ? entry.spent / entry.leads : 0;
    const leadsPercent = leadGoal ? (entry.leads / leadGoal) * 100 : 0;
    const paceTarget = entry.budget * paceFactor;
    const pacePercent = paceTarget ? ((entry.spent / paceTarget) - 1) * 100 : 0;

    return {
      ...entry,
      leadGoal,
      cplGoal,
      cpl,
      leadsPercent,
      pacePercent
    };
  };

  const aggregatedRows = useMemo(() => {
    const map = new Map();

    rows.forEach((row) => {
      const store = getStoreName(row);
      if (!map.has(store)) {
        map.set(store, {
          store,
          divisionGroup: getDivisionGroup(row),
          budget: 0,
          spent: 0,
          leadGoal: 0,
          leads: 0
        });
      }

      const entry = map.get(store);
      if (!entry.divisionGroup) {
        entry.divisionGroup = getDivisionGroup(row);
      }

      entry.spent += toNumber(row?.spend ?? row?.cost ?? row?.amount_spent);
      entry.leads += toNumber(row?.leads ?? row?.LEADS);
    });

    const withTargets = Array.from(map.values()).map((entry) => {
      const targets = targetsByStore.get(normalizeStoreKey(entry.store));
      if (targets) {
        if (targets.budget !== undefined) entry.budget = targets.budget;
        if (targets.leadGoal !== undefined) entry.leadGoal = targets.leadGoal;
      }
      return entry;
    });

    return withTargets.map((entry) => buildDerivedMetrics(entry));
  }, [rows, paceFactor, targetsByStore]);

  useEffect(() => {
    if (hasInitializedStores.current || aggregatedRows.length === 0) return;
    setSelectedStores(new Set(aggregatedRows.map((row) => row.store)));
    hasInitializedStores.current = true;
  }, [aggregatedRows]);

  const filteredRows = useMemo(() => {
    if (selectedStores.size === 0) return [];
    return aggregatedRows.filter((row) => selectedStores.has(row.store));
  }, [aggregatedRows, selectedStores]);

  const calculateTotals = (items) => {
    const totals = items.reduce(
      (acc, row) => {
        acc.budget += row.budget;
        acc.spent += row.spent;
        acc.leadGoal += row.leadGoal;
        acc.leads += row.leads;
        return acc;
      },
      { budget: 0, spent: 0, leadGoal: 0, leads: 0 }
    );

    return buildDerivedMetrics({ store: "Total", ...totals });
  };

  const sections = useMemo(() => {
    const oemRows = filteredRows.filter((row) => row.divisionGroup !== "Used");
    const usedRows = filteredRows.filter((row) => row.divisionGroup === "Used");
    return [
      { key: "OEM DIVISION", rows: oemRows },
      { key: "USED DIVISION", rows: usedRows }
    ];
  }, [filteredRows]);

  useEffect(() => {
    if (!storeMenuOpen) return undefined;
    const handleOutside = (event) => {
      if (storeMenuRef.current && !storeMenuRef.current.contains(event.target)) {
        setStoreMenuOpen(false);
      }
    };
    document.addEventListener("click", handleOutside);
    return () => document.removeEventListener("click", handleOutside);
  }, [storeMenuOpen]);

  useEffect(() => {
    if (!columnMenuOpen) return undefined;
    const handleOutside = (event) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target)) {
        setColumnMenuOpen(false);
      }
    };
    document.addEventListener("click", handleOutside);
    return () => document.removeEventListener("click", handleOutside);
  }, [columnMenuOpen]);

  const storeGroups = useMemo(() => {
    const groups = {
      "OEM Division": [],
      "Used Division": []
    };
    aggregatedRows.forEach((row) => {
      const key = row.divisionGroup === "Used" ? "Used Division" : "OEM Division";
      groups[key].push(row.store);
    });
    return groups;
  }, [aggregatedRows]);

  const visibleColumnList = useMemo(
    () => COLUMNS.filter((col) => visibleColumns.has(col.key)),
    [visibleColumns]
  );

  const storeLabel = useMemo(() => {
    if (selectedStores.size === 0) return "None selected";
    if (selectedStores.size === aggregatedRows.length && aggregatedRows.length > 0) return "All Dealerships";
    if (selectedStores.size <= 3) return Array.from(selectedStores).join(", ");
    return `${selectedStores.size} dealerships selected`;
  }, [selectedStores, aggregatedRows.length]);

  const columnLabel = useMemo(() => {
    const toggleableCount = COLUMNS.length - 1;
    const shownCount = visibleColumns.has("name") ? visibleColumns.size - 1 : visibleColumns.size;
    if (shownCount === toggleableCount) return "All Columns";
    if (shownCount <= 0) return "Name only";
    return `${shownCount} of ${toggleableCount} columns`;
  }, [visibleColumns]);

  const leadPctClass = (value) => {
    if (value === null || value === undefined) return "";
    if (value >= 80) return "clr-green";
    if (value >= 60) return "clr-yellow";
    return "clr-red";
  };

  const cplClass = (current, goal) => {
    if (!current || !goal) return "";
    const ratio = current / goal;
    if (ratio <= 1) return "clr-green";
    if (ratio <= 1.15) return "clr-yellow";
    return "clr-red";
  };

  const paceClass = (value) => {
    if (value === null || value === undefined) return "";
    const abs = Math.abs(value);
    if (abs <= 10) return "clr-green";
    if (abs <= 25) return "clr-yellow";
    return "clr-red";
  };

  const formatPace = (value) => {
    if (value === null || value === undefined) return "-";
    const label = value > 0 ? "Over" : "Under";
    const sign = value > 0 ? "+" : "";
    return `${label} ${sign}${value.toFixed(1)}%`;
  };

  const leadsChartData = useMemo(() => {
    const labels = filteredRows.map((row) => row.store);
    const pacedGoals = filteredRows.map((row) =>
      row.leadGoal ? Number((row.leadGoal * paceFactor).toFixed(1)) : 0
    );
    const achieved = filteredRows.map((row) => row.leads || 0);
    const achievedColors = filteredRows.map((row, index) => {
      const goal = pacedGoals[index];
      if (!goal) return "#808284";
      const ratio = achieved[index] / goal;
      if (ratio >= 1) return "#329044";
      if (ratio >= 0.8) return "#FFC62C";
      return "#EF3742";
    });

    return {
      labels,
      datasets: [
        {
          label: "Paced Goal",
          data: pacedGoals,
          backgroundColor: "rgba(65,64,66,0.18)",
          borderColor: "rgba(65,64,66,0.55)",
          borderWidth: 1,
          borderRadius: 4,
          barPercentage: 0.75,
          categoryPercentage: 0.75
        },
        {
          label: "Achieved",
          data: achieved,
          backgroundColor: achievedColors.map((color) => {
            const hex = color.replace("#", "");
            const r = parseInt(hex.slice(0, 2), 16);
            const g = parseInt(hex.slice(2, 4), 16);
            const b = parseInt(hex.slice(4, 6), 16);
            return `rgba(${r},${g},${b},0.82)`;
          }),
          borderRadius: 4,
          barPercentage: 0.75,
          categoryPercentage: 0.75
        }
      ]
    };
  }, [filteredRows, paceFactor]);

  const leadsChartOptions = useMemo(
    () => ({
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top",
          labels: { font: { family: "Rubik", size: 11 }, boxWidth: 14 }
        },
        datalabels: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.x.toFixed(1)} leads`
          }
        }
      },
      scales: {
        x: {
          grid: { color: "#f0f0f0" },
          ticks: { font: { family: "Rubik", size: 11 } },
          title: { display: true, text: "Leads", font: { family: "Rubik", size: 12 } }
        },
        y: {
          grid: { display: false },
          ticks: { font: { family: "Rubik", size: 11 } }
        }
      }
    }),
    []
  );

  const cplChartData = useMemo(() => {
    const labels = filteredRows.map((row) => row.store);
    const cplValues = filteredRows.map((row) => row.cpl || 0);
    const cplGoals = filteredRows.map((row) => row.cplGoal || 0);

    return {
      labels,
      datasets: [
        {
          label: "CPL Goal",
          data: cplGoals,
          backgroundColor: "rgba(65,64,66,0.2)",
          borderColor: "#414042",
          borderWidth: 1,
          borderRadius: 4,
          barPercentage: 0.75,
          categoryPercentage: 0.75,
          order: 1
        },
        {
          label: "CPL",
          data: cplValues,
          backgroundColor: "#F78E20",
          borderRadius: 4,
          barPercentage: 0.75,
          categoryPercentage: 0.75,
          order: 2
        }
      ]
    };
  }, [filteredRows]);

  const cplChartOptions = useMemo(
    () => ({
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top",
          labels: { font: { family: "Rubik", size: 11 }, boxWidth: 14 }
        },
        datalabels: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: $${ctx.parsed.x.toFixed(2)}`
          }
        }
      },
      scales: {
        x: {
          grid: { color: "#f0f0f0" },
          ticks: { callback: (value) => `$${value}`, font: { family: "Rubik", size: 11 } },
          title: { display: true, text: "Cost Per Lead ($)", font: { family: "Rubik", size: 12 } }
        },
        y: {
          grid: { display: false },
          ticks: { font: { family: "Rubik", size: 11 } }
        }
      }
    }),
    []
  );

  return (
    <div className="meta-mtd-dashboard">
      <header className="meta-mtd-header">
        <h1>
          <span>FFUN</span> Group | Meta Ads MTD
        </h1>
        <div className="meta-mtd-header-meta">
          <div>{dateLabel}</div>
          <div>Meta Ads Only</div>
        </div>
      </header>

      <div className="meta-mtd-container">
        <div className="meta-mtd-controls">
          <label>Dealerships:</label>
          <div className="meta-mtd-dropdown" ref={storeMenuRef}>
            <button
              type="button"
              className="meta-mtd-dropdown-btn"
              onClick={() => setStoreMenuOpen((prev) => !prev)}
            >
              {storeLabel}
            </button>
            <div className={`meta-mtd-dropdown-menu ${storeMenuOpen ? "open" : ""}`}>
              <div className="meta-mtd-dropdown-actions">
                <button
                  type="button"
                  onClick={() => setSelectedStores(new Set(aggregatedRows.map((row) => row.store)))}
                >
                  Select All
                </button>
                <button type="button" onClick={() => setSelectedStores(new Set())}>
                  Clear
                </button>
              </div>
              <div className="meta-mtd-dropdown-divider" />
              <div className="meta-mtd-dropdown-section">OEM Division</div>
              {storeGroups["OEM Division"].map((name) => {
                const checked = selectedStores.has(name);
                return (
                  <label className="meta-mtd-dropdown-item" key={`oem-${name}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = new Set(selectedStores);
                        if (checked) {
                          next.delete(name);
                        } else {
                          next.add(name);
                        }
                        setSelectedStores(next);
                      }}
                    />
                    {name}
                  </label>
                );
              })}
              <div className="meta-mtd-dropdown-divider" />
              <div className="meta-mtd-dropdown-section">Used Division</div>
              {storeGroups["Used Division"].map((name) => {
                const checked = selectedStores.has(name);
                return (
                  <label className="meta-mtd-dropdown-item" key={`used-${name}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = new Set(selectedStores);
                        if (checked) {
                          next.delete(name);
                        } else {
                          next.add(name);
                        }
                        setSelectedStores(next);
                      }}
                    />
                    {name}
                  </label>
                );
              })}
            </div>
          </div>

          <label>Columns:</label>
          <div className="meta-mtd-dropdown" ref={columnMenuRef}>
            <button
              type="button"
              className="meta-mtd-dropdown-btn"
              onClick={() => setColumnMenuOpen((prev) => !prev)}
            >
              {columnLabel}
            </button>
            <div className={`meta-mtd-dropdown-menu ${columnMenuOpen ? "open" : ""}`}>
              <div className="meta-mtd-dropdown-actions">
                <button
                  type="button"
                  onClick={() => setVisibleColumns(new Set(COLUMNS.map((col) => col.key)))}
                >
                  Show All
                </button>
                <button
                  type="button"
                  onClick={() => setVisibleColumns(new Set(["name"]))}
                >
                  Hide All
                </button>
              </div>
              <div className="meta-mtd-dropdown-divider" />
              {COLUMNS.filter((col) => col.key !== "name").map((col) => {
                const checked = visibleColumns.has(col.key);
                return (
                  <label className="meta-mtd-dropdown-item" key={col.key}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = new Set(visibleColumns);
                        if (checked) {
                          next.delete(col.key);
                        } else {
                          next.add(col.key);
                        }
                        setVisibleColumns(next);
                      }}
                    />
                    {col.label}
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        {error && <div className="meta-mtd-error">{error}</div>}
        {loading && <div className="meta-mtd-loading">Loading Meta MTD data...</div>}

        <div className="meta-mtd-table-wrap">
          <table>
            <thead>
              <tr>
                {visibleColumnList.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 && !loading ? (
                <tr>
                  <td colSpan={visibleColumnList.length} className="meta-mtd-empty">
                    No Meta MTD data found.
                  </td>
                </tr>
              ) : (
                sections.map((section) => (
                  <React.Fragment key={section.key}>
                    <tr className="meta-mtd-division">
                      <td colSpan={visibleColumnList.length}>{section.key}</td>
                    </tr>
                    {section.rows.map((row) => (
                      <tr key={row.store}>
                        {visibleColumnList.map((col) => {
                          const value = row[col.key];
                          let display = value;
                          if (col.key === "name") display = row.store;
                          if (col.key === "budget") display = formatCurrency(row.budget);
                          if (col.key === "spent") display = formatCurrency(row.spent);
                          if (col.key === "budgetPace") display = formatPace(row.pacePercent);
                          if (col.key === "leadGoal") display = formatNumber(row.leadGoal);
                          if (col.key === "leads") display = formatNumberOne(row.leads);
                          if (col.key === "leadsPercent") display = formatPercent(row.leadsPercent);
                          if (col.key === "cplGoal") display = formatCurrency(row.cplGoal);
                          if (col.key === "cpl") display = formatCurrency(row.cpl);

                          let className = "";
                          if (col.key === "leadsPercent") className = leadPctClass(row.leadsPercent);
                          if (col.key === "cpl") className = cplClass(row.cpl, row.cplGoal);
                          if (col.key === "budgetPace") className = paceClass(row.pacePercent);

                          return (
                            <td key={col.key} className={className}>
                              {display}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    {section.rows.length > 0 && (
                      <tr className="meta-mtd-total">
                        {(() => {
                          const totals = calculateTotals(section.rows);
                          return visibleColumnList.map((col) => {
                            let display = totals[col.key];
                            if (col.key === "name") display = `${section.key.split(" ")[0]} Total`;
                            if (col.key === "budget") display = formatCurrency(totals.budget);
                            if (col.key === "spent") display = formatCurrency(totals.spent);
                            if (col.key === "budgetPace") display = formatPace(totals.pacePercent);
                            if (col.key === "leadGoal") display = formatNumber(totals.leadGoal);
                            if (col.key === "leads") display = formatNumberOne(totals.leads);
                            if (col.key === "leadsPercent") display = formatPercent(totals.leadsPercent);
                            if (col.key === "cplGoal") display = formatCurrency(totals.cplGoal);
                            if (col.key === "cpl") display = formatCurrency(totals.cpl);
                            return <td key={col.key}>{display}</td>;
                          });
                        })()}
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="meta-mtd-charts">
          <div className="meta-mtd-chart-card">
            <h3>Goal vs Achieved Leads MTD ({dateLabel})</h3>
            <div className="meta-mtd-chart-container">
              <Bar data={leadsChartData} options={leadsChartOptions} />
            </div>
          </div>
          <div className="meta-mtd-chart-card">
            <h3>CPL by Dealership</h3>
            <div className="meta-mtd-chart-container">
              <Bar data={cplChartData} options={cplChartOptions} />
            </div>
          </div>
        </div>

        <div className="meta-mtd-footer">
          Source: Meta Marketing API | Goals from marketing targets | Date range {dateLabel}
        </div>
      </div>
    </div>
  );
}
